/*
 * Fitment in the predictive search panel, for shoppers with an active machine
 * (RpmMachineContext: explicit pick, then garage, the machine the app's badges
 * use). Typed product suggestions rank direct fits, then universal fits, then
 * no fitment data, then doesn't fit, each group in Shopify's relevance order.
 * Recently viewed cards keep their recency order. Only the positive "Fits"
 * badge shows: the app hides the rest because negative stamps depressed
 * conversion in its own tests.
 *
 * A/B contract: the Intelligems control arm injects
 *   :root { --rpm-search-fitment: off !important; }
 *   predictive-search .rpm-fitment-badge { display: none !important; }
 * Both arms request the same candidates and report the same exposure, so they
 * differ only in what the shopper sees.
 */
(function () {
  'use strict';

  if (theme.searchFitment) return;

  // Shopify's predictive search maximum.
  var CANDIDATE_LIMIT = 10;
  // Liquid resolves at most this many metaobject references, so a list at the
  // cap may be missing machines (as in the app's fitment-badge-trust.js).
  var LIQUID_REFERENCE_CAP = 50;
  var ARM_PROPERTY = '--rpm-search-fitment';
  var ARM_OFF = 'off';
  var BADGE_SELECTOR = '[data-rpm-fitment-badge]';
  var LIST_SELECTOR = '.results--products [data-visible-limit]';
  var UNIVERSAL_ATTR = 'data-universal-fit';
  // fitment-badge.js reads it as dataset.apiFetched: the list is authoritative.
  var API_SETTLED_ATTR = 'data-api-fetched';
  var PRODUCT_ID_ATTR = 'data-product-id';
  // Stamped in both arms, so click goals can target it without an arm bias.
  var FIT_ATTR = 'data-search-fit';
  // On the list when any candidate, shown or not, fits the machine: the new search's categories follow the machine then.
  var ANY_FIT_ATTR = 'data-search-any-fit';
  var EXPOSURE_EVENT = 'rpm_search_fit_exposure';

  var FITS = 'fits';
  var UNIVERSAL = 'universal';
  var NEUTRAL = 'neutral';
  // A capped list without the machine may still fit: it ranks as neutral and
  // keeps its badge for the app to settle against the full list.
  var UNKNOWN = 'unknown';
  var NO_FIT = 'no-fit';
  var RANK = { fits: 0, universal: 1, neutral: 2, unknown: 2, 'no-fit': 3 };

  var exposed = {};
  var selectedMachine = null;
  var machineLoading = false;

  // The reporter is the fitment app's code: search must not depend on it working.
  function report(error, action) {
    try {
      if (typeof window.rpmReportError === 'function') {
        window.rpmReportError(error, { script: 'search-fitment', action: action });
      }
    } catch (e) { /* nowhere left to report to */ }
  }

  // fitment-badge.js badges against the last selection event, which a URL-shared
  // filter link raises without saving it, and otherwise against RpmMachineContext.
  // The order follows the same machine. While that link's machine is still being
  // looked up the app shows no verdict, so search ranks nothing either.
  document.addEventListener('rpm:machine:selected', function (event) {
    var detail = event && event.detail;
    var id = detail && typeof detail.metaobjectId === 'string' ? detail.metaobjectId.trim() : '';
    if (!id) return;
    selectedMachine = { id: id, name: detail.displayName || '' };
    machineLoading = false;
  });
  document.addEventListener('rpm:machine:cleared', function () {
    selectedMachine = null;
    machineLoading = false;
  });
  document.addEventListener('rpm:machine:loading', function () {
    machineLoading = true;
  });
  document.addEventListener('rpm:machine:unresolved', function () {
    machineLoading = false;
  });

  function bucketOf(item, ids, machineId) {
    if (item.hasAttribute(UNIVERSAL_ATTR)) return UNIVERSAL;
    if (!ids) return NEUTRAL;
    for (var i = 0; i < ids.length; i++) {
      if (typeof ids[i] === 'string' && ids[i].trim() === machineId) return FITS;
    }
    return ids.length >= LIQUID_REFERENCE_CAP ? UNKNOWN : NO_FIT;
  }

  function badgeIds(item) {
    var badge = item.querySelector(BADGE_SELECTOR);
    if (!badge) return null;
    try {
      var parsed = JSON.parse(badge.getAttribute('data-machine-ids') || '');
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      report(e, 'parse-machine-ids');
      return null;
    }
  }

  function rankByBucket(buckets) {
    return buckets
      .map(function (bucket, index) { return { rank: RANK[bucket], index: index }; })
      .sort(function (a, b) { return a.rank - b.rank || a.index - b.index; })
      .map(function (entry) { return entry.index; });
  }

  // Exposure fires, in both arms, only where the treatment would look different,
  // so the analysis compares the shoppers this feature could reach.
  function wouldDiffer(buckets, treatmentShown, controlShown) {
    if (treatmentShown.join() !== controlShown.join()) return true;
    return treatmentShown.some(function (i) {
      return buckets[i] === FITS || buckets[i] === UNKNOWN;
    });
  }

  // The new search's machine card can switch the machine off for search (search-machine-switch.js): no ranking, no badges.
  function switchedOff(machine) {
    var machineSwitch = theme.searchMachineSwitch;
    return !!(machine && machineSwitch && machineSwitch.isOff(machine));
  }

  function activeMachine() {
    var machine = resolvedMachine();
    return switchedOff(machine) ? null : machine;
  }

  function resolvedMachine() {
    if (machineLoading) return null;
    if (selectedMachine) return selectedMachine;
    var ctx = window.RpmMachineContext;
    if (!ctx || typeof ctx.getActiveMachine !== 'function') return null;
    try {
      var machine = ctx.getActiveMachine();
      var id = machine && typeof machine.metaobjectId === 'string' ? machine.metaobjectId.trim() : '';
      return id ? { id: id, name: machine.displayName || '' } : null;
    } catch (e) {
      report(e, 'active-machine');
      return null;
    }
  }

  function isTreatment() {
    return getComputedStyle(document.documentElement).getPropertyValue(ARM_PROPERTY).trim() !== ARM_OFF;
  }

  // Badges show in the treatment arm, unless the shopper switched their machine off for search.
  function showsFitment() {
    return isTreatment() && !switchedOff(resolvedMachine());
  }

  function removeBadge(item) {
    var badge = item.querySelector(BADGE_SELECTOR);
    if (badge) badge.remove();
  }

  // fitment-badge.js reveals badges only once they are in the page, which would
  // shift the row; a fit already known here paints with it. The app's later
  // pass shows the same state, or hides it if its fresh data disagrees.
  function revealFit(item, machineName) {
    var badge = item.querySelector(BADGE_SELECTOR);
    if (!badge) return;
    Array.prototype.forEach.call(badge.querySelectorAll('[data-badge-state]'), function (state) {
      state.hidden = state.getAttribute('data-badge-state') !== FITS;
    });
    var name = badge.querySelector('[data-machine-name]');
    if (name) name.textContent = machineName;
    badge.hidden = false;
  }

  // fitment-badge.js trusts a complete list rendered with the page and asks the
  // fitment API for the rest. These lists were rendered by this very request, so
  // the same trust holds. Without a product id the app neither fetches the badge
  // nor writes another fetch's answer onto it (a failed fetch answers every id
  // with an empty list), yet still badges it for the current machine: search then
  // makes no fitment API call and an API outage cannot take a correct Fits away.
  function settleFreshList(item, ids) {
    var badge = item.querySelector(BADGE_SELECTOR);
    if (!badge || !ids || ids.length >= LIQUID_REFERENCE_CAP) return;
    badge.setAttribute(API_SETTLED_ATTR, 'true');
    badge.removeAttribute(PRODUCT_ID_ATTR);
  }

  function applyBadge(item, bucket, treatment, machine) {
    if (!treatment || bucket === NO_FIT) removeBadge(item);
    else if (bucket === FITS) revealFit(item, machine.name);
  }

  // Intelligems filters on whether a visitor triggered the event, not how often.
  // Intelligems replaces igEvents with its own queue; measurement must never cost
  // the shopper their results. A push that throws is retried on the next search.
  function recordExposure(surface) {
    if (exposed[surface]) return;
    try {
      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: EXPOSURE_EVENT, properties: { surface: surface } });
      exposed[surface] = true;
    } catch (e) {
      report(e, 'record-exposure');
    }
  }

  function candidateLimit() {
    return activeMachine() ? CANDIDATE_LIMIT : null;
  }

  // The new search's machine (its card, categories and best sellers, in either arm): null while a shared link's is looked up; else
  // the active machine, or the one switched off for search with the garage's machines the card offers instead.
  function machineStatus() {
    if (machineLoading) return null;
    var machine = resolvedMachine();
    return switchedOff(machine) ? { machine: null, off: machine, choices: garageChoices(machine) } : { machine: machine };
  }

  // The machine switched off first, then the rest of the garage the fitment app has loaded (fitment-init.js), each once, by name.
  function garageChoices(off) {
    var data = window.rpmFitmentData;
    var ids = [off.id];
    var names = [off.name];
    (data && Array.isArray(data.vehicles) ? data.vehicles : []).forEach(function (vehicle) {
      if (!vehicle || !vehicle.metaobjectId || ids.indexOf(vehicle.metaobjectId) !== -1) return;
      ids.push(vehicle.metaobjectId);
      names.push(vehicle.displayName || '');
    });
    return names;
  }

  // root is the parsed response, not yet in the page. Liquid renders every
  // candidate past the visible limit hidden, so if this fails the panel shows
  // the first suggestions in relevance order, as without this module.
  function prepareResults(root) {
    try {
      var list = root && root.querySelector(LIST_SELECTOR);
      if (!list) return;
      var items = Array.prototype.slice.call(list.children);
      var limit = parseInt(list.getAttribute('data-visible-limit'), 10) || items.length;
      var machine = activeMachine();
      var treatment = showsFitment();
      var controlShown = items.slice(0, limit).map(function (item, i) { return i; });
      var shown = controlShown;
      var buckets = null;

      var lists = items.map(badgeIds);
      if (machine) {
        buckets = items.map(function (item, i) { return bucketOf(item, lists[i], machine.id); });
        if (buckets.indexOf(FITS) !== -1) list.setAttribute(ANY_FIT_ATTR, '');
        var treatmentShown = rankByBucket(buckets).slice(0, limit);
        if (treatment) shown = treatmentShown;
        if (wouldDiffer(buckets, treatmentShown, controlShown)) recordExposure('results');
      }

      var kept = shown.map(function (i) {
        return { item: items[i], ids: lists[i], bucket: buckets ? buckets[i] : null };
      });
      list.replaceChildren.apply(list, kept.map(function (entry) { return entry.item; }));
      kept.forEach(function (entry) {
        entry.item.hidden = false;
        if (entry.bucket) entry.item.setAttribute(FIT_ATTR, entry.bucket);
      });
      kept.forEach(function (entry) {
        applyBadge(entry.item, entry.bucket, treatment, machine);
        if (treatment) settleFreshList(entry.item, entry.ids);
      });
    } catch (e) {
      report(e, 'prepare-results');
    }
  }

  // items are the recently viewed <li>s before they are added to the panel.
  function prepareRecent(items) {
    try {
      var machine = activeMachine();
      var treatment = showsFitment();
      var reachable = false;
      items.forEach(function (item) {
        var bucket = machine ? bucketOf(item, badgeIds(item), machine.id) : null;
        if (bucket) item.setAttribute(FIT_ATTR, bucket);
        if (bucket === FITS || bucket === UNKNOWN) reachable = true;
        applyBadge(item, bucket, treatment, machine);
      });
      if (!reachable) return;
      // PredictiveSearch drops this render if the shopper types during the
      // fetch. It commits in the microtasks that follow, so a task later the
      // cards are in the page only if the shopper actually saw them.
      setTimeout(function () {
        if (items.some(function (item) { return item.isConnected; })) recordExposure('recent');
      }, 0);
    } catch (e) {
      report(e, 'prepare-recent');
    }
  }

  theme.searchFitment = {
    candidateLimit: candidateLimit,
    machineStatus: machineStatus,
    prepareResults: prepareResults,
    prepareRecent: prepareRecent
  };
})();
